import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';

import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import type { QuoteAttachment } from '@/utils/quote_attachment_utils';

import { type Receipt, fetchReceiptBlob } from './receiptApi';

export type ReceiptPreviewTarget = Pick<
    Receipt,
    'id' | 'original_filename'
> &
    Partial<Pick<Receipt, 'merchant' | 'content_type'>>;

type Props = {
    receipt: ReceiptPreviewTarget | null;
    onClose: () => void;
};

const EXTENSION_BY_TYPE: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/heic': '.heic',
    'image/heif': '.heic',
};

function filenameForContentType(name: string, contentType: string): string {
    const ext = EXTENSION_BY_TYPE[contentType.toLowerCase()];
    if (!ext) return name;
    if (name.toLowerCase().endsWith(ext)) return name;
    const base = name.replace(/\.[a-z0-9]+$/i, '') || name;
    return `${base}${ext}`;
}

export function ReceiptPreviewDialog({ receipt, onClose }: Props) {
    const notify = useNotify();
    const [attachment, setAttachment] = useState<QuoteAttachment | null>(null);
    const [sourceUrl, setSourceUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!receipt) {
            setAttachment(null);
            setSourceUrl(current => {
                if (current) URL.revokeObjectURL(current);
                return null;
            });
            return undefined;
        }

        let cancelled = false;
        let objectUrl: string | null = null;
        setAttachment({
            id: `receipt-${receipt.id}`,
            name: receipt.merchant || receipt.original_filename,
            filename: receipt.original_filename,
            content_type: receipt.content_type || 'application/octet-stream',
        });
        setSourceUrl(null);

        void fetchReceiptBlob(receipt.id)
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSourceUrl(objectUrl);
                const contentType = (
                    receipt.content_type ||
                    blob.type ||
                    'application/octet-stream'
                )
                    .split(';')[0]
                    .trim();
                const filename = filenameForContentType(
                    receipt.original_filename,
                    contentType,
                );
                setAttachment(current =>
                    current
                        ? {
                              ...current,
                              filename,
                              name: receipt.merchant || filename,
                              content_type: contentType,
                          }
                        : current
                );
            })
            .catch(error => {
                if (cancelled) return;
                notify(
                    error instanceof Error ? error.message : 'Could not open receipt',
                    { type: 'error' }
                );
                onClose();
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [receipt, notify, onClose]);

    const handleClose = () => {
        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        setSourceUrl(null);
        setAttachment(null);
        onClose();
    };

    return (
        <QuoteAttachmentPreviewDialog
            attachment={attachment}
            sourceUrl={sourceUrl}
            loading={!!attachment && !sourceUrl}
            onClose={handleClose}
        />
    );
}
